import React, { useEffect, useState } from 'react';
import PerinHealthGrid from '../../components/PerinHealthGrid';
import { setSelectedRows, setSelectedItem, setSelectedHeaders } from '../../components/PerinHealthGridSlice';
import { resetFilterState } from '../../slicers/FilterSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useAxios from '../../services/AxiosConfig';
import FilterManagementView from '../../components/FilterManagementView';
import managementStyles from '../ManagementPages.module.css';
import { LeftArrowIcon } from '../../components/icons/Icons';
import { configureBatteryIcon } from '../../utils/batteryUtils';
import titleStyles from '../hub-management/HubManagement.module.css';
import { Button, Modal, TextField } from '@mui/material';
import { Box } from '@mui/system';
import { formatDateString } from '../../utils/dateUtils';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import { fetchDeviceTrackingData } from '../../utils/deviceTrackingUtils';

const DeviceAssignment = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const httpService = useAxios(axiosConfig.envURL);
    const userDateFormat = useSelector((state) => state.userInfo.dateFormat);
    useEffect(() => {
        dispatch(resetFilterState());
        dispatch(setSelectedRows([]));
        dispatch(setSelectedItem({}));
    }, []);
    const {groupName, totalDevices, deviceName, groupDeviceId, serialNums} = useSelector((state) => state.deviceManagement.assignmentData);

    const [deviceData, setDeviceData] = useState([]);
    const [activeTab, setActiveTab] = useState('assigned');
    const [filteredDeviceData, setFilteredDeviceData] = useState([]);
    const [showCheckoutModal, setShowCheckoutModal] = useState(false);
    const [checkoutForm, setCheckoutForm] = useState({
        name: '',
        reason: '',
        system: ''
    });
    
    const selectedRows = useSelector((state) => state.perinHealthGrid.selectedRows);

    // Add new state for checkout log data
    const [checkoutLogData, setCheckoutLogData] = useState([]);
    
    // Add state for alerts/errors data
    const [alertsData, setAlertsData] = useState({});
    
    // Add state for fall detection alerts
    const [fallAlertsData, setFallAlertsData] = useState({});
    
    // Add state for latest recording timestamps
    const [latestRecordingTimestamps, setLatestRecordingTimestamps] = useState({});
    
    // Add state for on-track status
    const [deviceTrackingStatus, setDeviceTrackingStatus] = useState({});
    
    // Add state for metadata availability
    const [deviceMetadataStatus, setDeviceMetadataStatus] = useState({});
    
    // Add state for off-track durations
    const [offTrackDurations, setOffTrackDurations] = useState({});
    
    // Add state for reboot counts
    const [rebootCounts, setRebootCounts] = useState({});
    
    // Add state for last reboot timestamps
    const [lastRebootTimestamps, setLastRebootTimestamps] = useState({});
    
    // Add state for device schedules
    const [deviceSchedules, setDeviceSchedules] = useState({});

    useEffect(() => {
        const fetchData = async () => {
            // Fetch alerts and tracking data in parallel
            const [alertsData, trackingData] = await Promise.all([
                fetchAlertsForAllDevices(),
                fetchLatestRecordingTimestamps()
            ]);
            await getDeviceAssignments(alertsData.alerts, trackingData.timestamps, trackingData.trackingStatus, trackingData.metadataStatus, alertsData.fallAlerts, trackingData.offTrackDurations, trackingData.rebootCounts, trackingData.lastRebootTimestamps, trackingData.batteryLevels, trackingData.deviceSchedules);
        };
        fetchData();
    }, [userDateFormat]);

    useEffect(() => {
        if (selectedRows.length > 0) {
            console.log('Selected Rows Status:', selectedRows.map(row => row.checkInOutStatus));
        }
    }, [selectedRows]);

    const handlePatientNameClick = (device) => {
        // Create a clean version of the device data without React elements
        const cleanDevice = {
            ...device,
            // Remove React elements and complex objects
            status: typeof device.status === 'object' ? device.statusText : device.status,
            checkInOutStatus: typeof device.checkInOutStatus === 'object' ? 
                (device.checkInOutStatus.props.children === 'Checked Out' ? 'Checked Out' : 'Checked In') : 
                device.checkInOutStatus,
            // Remove the Status React element
            Status: undefined,
            // Remove the errorStatus React element (keep as string)
            errorStatus: typeof device.errorStatus === 'object' ? 'Error Present' : device.errorStatus,
            // Remove the fallDetection React element (keep as string)
            fallDetection: typeof device.fallDetection === 'object' ? 'Fall Detected' : device.fallDetection,
            // Keep rebootCount and lastReboot as they are already strings/numbers
        };

        navigate('/device-information', { 
            state: { 
                deviceData: cleanDevice 
            } 
        });
    };

    // Modify headers based on the active tab
    const headers = activeTab === 'checkoutLog' ? [
        { key: 'serialNumber', label: "Serial Number"},
        { key: 'status', label: "Battery Status"},
        { key: 'userName', label: "Name"},
        { key: 'reason', label: "Reason"},
        { key: 'software', label: "Software"},
        { key: 'checkoutDate', label: "Check Out Date"},
        { key: 'checkinDate', label: "Check In Date"}
    ] : (deviceName === "Perin Patch" ? [
        { key: 'patientId', label: "ID"},
        { key: 'patientName', label: "Assigned to"},
        { key: 'serialNumber', label: "Serial Number"},
        { key: 'macAddress', label: "Mac Address"},
        { key: 'assigned', label: "Date Assigned"},
        { key: 'status', label: "Battery Status"},
        { key: 'Status', label: "Status"},
        { key: 'errorStatus', label: "Error Status"},
        { key: 'fallDetection', label: "Fall Detection"},
        { key: 'latestRecording', label: "Latest Recording"},
        { key: 'rebootCount', label: "Reboot Count"},
        { key: 'lastReboot', label: "Last Reboot"},
        
    ] : [
        { key: 'patientId', label: "ID"},
        { key: 'patientName', label: "Assigned to"},
        { key: 'serialNumber', label: "Serial Number"},
        { key: 'macAddress', label: "Mac Address"},
        { key: 'assigned', label: "Date Assigned"},
    ]);

    // Modify useEffect to handle data based on active tab
    useEffect(() => {
        if (deviceData.length > 0) {
            let filtered;
            if (activeTab === 'checkoutLog') {
                filtered = checkoutLogData;
            } else {
                filtered = deviceData.filter(device => 
                    activeTab === 'assigned' ? device.isAssigned : !device.isAssigned
                ).map(device => ({
                    ...device,
                    patientName: device.patientName || "-",
                    checkInOutStatus: device.checkInOutStatus ? (
                        <span style={{
                            color: device.checkInOutStatus === 'Checked Out' ? '#FF4444' : '#006969',
                            fontWeight: '500'
                        }}>
                            {device.checkInOutStatus === 'Checked Out' ? 'Checked Out' : 'Checked In'}
                        </span>
                    ) : '-'
                }));
            }
            setFilteredDeviceData(filtered);
        }
    }, [deviceData, activeTab, checkoutLogData, alertsData, latestRecordingTimestamps, deviceTrackingStatus, deviceMetadataStatus, fallAlertsData, offTrackDurations, rebootCounts, lastRebootTimestamps]);

    // Add useEffect to fetch checkout log when tab changes
    useEffect(() => {
        if (activeTab === 'checkoutLog') {
            getCheckoutLog();
        }
    }, [activeTab]);

    useEffect(() => {
        if (activeTab === 'checkoutLog') {
            // Set all headers as selected when in checkout log tab
            dispatch(setSelectedHeaders(headers));
        }
    }, [activeTab, headers]);

    const fetchAlertsForAllDevices = async () => {
        try {
            const accountIds = serialNums.filter((item) => item.isAssigned).map(patient => patient.patientId);
            
            if (accountIds.length === 0) return { alerts: {}, fallAlerts: {} };

            // Fetch alerts for all patients
            const alertPromises = accountIds.map(async (patientId) => {
                try {
                    const response = await httpService.get(`/alerts/${patientId}?deviceStatus=true`);
                    if ((response.data?.success || response.data?.code === 200) && response.data?.data && Array.isArray(response.data.data)) {
                        // Filter fall alerts (alert_subType === "fall status") - only unresolved
                        const fallAlerts = response.data.data.filter(alert => 
                            alert.alert_subType === "fall status" && 
                            alert.alert_status !== "resolved"
                        );
                        
                        // Get the most recent fall alert
                        const latestFallAlert = fallAlerts.length > 0 
                            ? fallAlerts.sort((a, b) => new Date(b.alert_lastUpdatedTimestamp) - new Date(a.alert_lastUpdatedTimestamp))[0]
                            : null;
                        
                        // Get the most recent non-fall alert for status column
                        const nonFallAlerts = response.data.data.filter(alert => 
                            alert.alert_subType !== "fall status"
                        );
                        const sortedAlerts = nonFallAlerts.sort((a, b) => {
                            // For Bluetooth errors, use createdDate; otherwise use alert_lastUpdatedTimestamp
                            const getAlertTimestamp = (alert) => {
                                if (alert.alert_title && alert.alert_title.includes("Bluetooth")) {
                                    return alert.createdDate;
                                }
                                return alert.alert_lastUpdatedTimestamp;
                            };
                            
                            const timestampA = getAlertTimestamp(a);
                            const timestampB = getAlertTimestamp(b);
                            
                            return new Date(timestampB || 0) - new Date(timestampA || 0);
                        });
                        
                        return { 
                            patientId, 
                            alert: sortedAlerts[0] || null,
                            fallAlert: latestFallAlert
                        };
                    }
                    return { patientId, alert: null, fallAlert: null };
                } catch (err) {
                    console.error(`Error fetching alerts for patient ${patientId}:`, err);
                    return { patientId, alert: null, fallAlert: null };
                }
            });

            const alertResults = await Promise.all(alertPromises);
            
            // Convert to objects keyed by patientId
            const alertsMap = alertResults.reduce((acc, { patientId, alert }) => {
                acc[patientId] = alert;
                return acc;
            }, {});
            
            const fallAlertsMap = alertResults.reduce((acc, { patientId, fallAlert }) => {
                acc[patientId] = fallAlert;
                return acc;
            }, {});
            
            setAlertsData(alertsMap);
            setFallAlertsData(fallAlertsMap);
            return { alerts: alertsMap, fallAlerts: fallAlertsMap };
        } catch (err) {
            console.error('Error fetching alerts:', err);
            return { alerts: {}, fallAlerts: {} };
        }
    };

    const fetchLatestRecordingTimestamps = async () => {
        try {
            const accountIds = serialNums.filter((item) => item.isAssigned).map(patient => patient.patientId);
            
            if (accountIds.length === 0) return { timestamps: {}, trackingStatus: {}, metadataStatus: {}, offTrackDurations: {} };

            // Fetch patch metadata and device tracking data for all patients
            const trackingPromises = accountIds.map(async (patientId) => {
                try {
                    // First fetch patch metadata to get device schedule, reboot count, battery, and last reboot time
                    let patchMetadata = null;
                    let hasMetadata = false;
                    let rebootCount = null;
                    let lastRebootTimestamp = null;
                    let batteryPercentage = null;
                    try {
                        // Fetch full metadata array to analyze reboot history
                        const metadataArrayResponse = await httpService.get(`/measurements/patch/metadata/${patientId}`);
                        if ((metadataArrayResponse.data?.success || metadataArrayResponse.data?.code === 200) && 
                            metadataArrayResponse.data?.data && 
                            Array.isArray(metadataArrayResponse.data.data) && 
                            metadataArrayResponse.data.data.length > 0) {
                            
                            // Sort by createdDate descending to get most recent first
                            const sortedMetadata = [...metadataArrayResponse.data.data].sort((a, b) => 
                                new Date(b.createdDate) - new Date(a.createdDate)
                            );
                            
                            // Use the latest metadata for device schedule
                            patchMetadata = sortedMetadata[0];
                            hasMetadata = true;
                            rebootCount = patchMetadata.rebootCount || null;
                            
                            // Extract battery level and convert from decimal to percentage
                            const batteryLevel = patchMetadata.batteryLevel;
                            batteryPercentage = batteryLevel ? (batteryLevel > 1 ? batteryLevel : batteryLevel * 100) : null;
                            
                            // Find when the reboot count last increased
                            if (sortedMetadata.length > 1 && rebootCount !== null) {
                                // Look through sorted metadata to find where reboot count changed
                                for (let i = 0; i < sortedMetadata.length - 1; i++) {
                                    const currentRebootCount = sortedMetadata[i].rebootCount || 0;
                                    const previousRebootCount = sortedMetadata[i + 1].rebootCount || 0;
                                    
                                    if (currentRebootCount > previousRebootCount) {
                                        // Found where reboot count increased
                                        lastRebootTimestamp = sortedMetadata[i].createdDate;
                                        break;
                                    }
                                }
                                
                                // If no increase found, use the latest metadata timestamp
                                if (!lastRebootTimestamp) {
                                    lastRebootTimestamp = patchMetadata.createdDate;
                                }
                            } else {
                                // Single metadata entry or no reboot count, use latest timestamp
                                lastRebootTimestamp = patchMetadata.createdDate || null;
                            }
                        } else {
                            // If array is empty, try fetching latest metadata as fallback
                            const latestMetadataResponse = await httpService.get(`/measurements/patch/metadata/latest/${patientId}`);
                            if ((latestMetadataResponse.data?.success || latestMetadataResponse.data?.code === 200) && latestMetadataResponse.data?.data) {
                                patchMetadata = latestMetadataResponse.data.data;
                                hasMetadata = true;
                                rebootCount = patchMetadata.rebootCount || null;
                                lastRebootTimestamp = patchMetadata.createdDate || null;
                                
                                // Extract battery level and convert from decimal to percentage
                                const batteryLevel = patchMetadata.batteryLevel;
                                batteryPercentage = batteryLevel ? (batteryLevel > 1 ? batteryLevel : batteryLevel * 100) : null;
                            }
                        }
                    } catch (metaErr) {
                        // Check if it's a 404 error with "No HealthPatch metadata found"
                        if (metaErr.response?.data?.code === 404 && metaErr.response?.data?.message === "No HealthPatch metadata found") {
                            hasMetadata = false;
                        }
                        console.error(`Error fetching metadata for patient ${patientId}:`, metaErr);
                    }

                    // Then fetch device tracking data with metadata for on-track calculation
                    const { latestTimestamp, isOnTrack, hasMetadata: trackingHasMetadata, readings } = await fetchDeviceTrackingData(
                        patientId,
                        httpService,
                        patchMetadata
                    );

                    // Calculate off-track duration if device is off track
                    let offTrackDuration = null;
                    if (!isOnTrack && hasMetadata && patchMetadata?.deviceSchedule && readings && readings.length > 0) {
                        const currentTime = new Date();
                        const sortedReadings = [...readings].sort((a, b) => 
                            new Date(b.completedDate) - new Date(a.completedDate)
                        );
                        const latestReading = sortedReadings[0];
                        
                        if (latestReading) {
                            const timeDeltaMs = currentTime - new Date(latestReading.completedDate);
                            const timeDeltaMinutes = timeDeltaMs / (1000 * 60);
                            
                            // Find the fastest frequency to determine expected time
                            const modalityFrequencies = [
                                patchMetadata.deviceSchedule.ecgMins,
                                patchMetadata.deviceSchedule.ppgMins,
                                patchMetadata.deviceSchedule.audioMins,
                                patchMetadata.deviceSchedule.tempMins,
                                patchMetadata.deviceSchedule.biozMins
                            ].filter(freq => freq != null && freq > 0);
                            
                            if (modalityFrequencies.length > 0) {
                                // Calculate total time since last reading (total time off track)
                                offTrackDuration = timeDeltaMinutes;
                            }
                        }
                    }

                    return { 
                        patientId, 
                        timestamp: latestTimestamp, 
                        isOnTrack, 
                        hasMetadata: hasMetadata && trackingHasMetadata,
                        offTrackDuration,
                        rebootCount,
                        lastRebootTimestamp,
                        batteryLevel: batteryPercentage,
                        deviceSchedule: patchMetadata?.deviceSchedule || null
                    };
                } catch (err) {
                    console.error(`Error fetching tracking data for patient ${patientId}:`, err);
                    return { patientId, timestamp: null, isOnTrack: true, hasMetadata: false, offTrackDuration: null, rebootCount: null, lastRebootTimestamp: null, batteryLevel: null, deviceSchedule: null };
                }
            });

            const trackingResults = await Promise.all(trackingPromises);
            
            // Convert to objects keyed by patientId
            const timestampsMap = trackingResults.reduce((acc, { patientId, timestamp }) => {
                acc[patientId] = timestamp;
                return acc;
            }, {});

            const trackingStatusMap = trackingResults.reduce((acc, { patientId, isOnTrack }) => {
                acc[patientId] = isOnTrack;
                return acc;
            }, {});

            const metadataStatusMap = trackingResults.reduce((acc, { patientId, hasMetadata }) => {
                acc[patientId] = hasMetadata;
                return acc;
            }, {});

            const offTrackDurationsMap = trackingResults.reduce((acc, { patientId, offTrackDuration }) => {
                acc[patientId] = offTrackDuration;
                return acc;
            }, {});

            const rebootCountsMap = trackingResults.reduce((acc, { patientId, rebootCount }) => {
                acc[patientId] = rebootCount;
                return acc;
            }, {});

            const lastRebootTimestampsMap = trackingResults.reduce((acc, { patientId, lastRebootTimestamp }) => {
                acc[patientId] = lastRebootTimestamp;
                return acc;
            }, {});

            const batteryLevelsMap = trackingResults.reduce((acc, { patientId, batteryLevel }) => {
                acc[patientId] = batteryLevel;
                return acc;
            }, {});
            
            const deviceSchedulesMap = trackingResults.reduce((acc, { patientId, deviceSchedule }) => {
                acc[patientId] = deviceSchedule;
                return acc;
            }, {});
            
            setLatestRecordingTimestamps(timestampsMap);
            setDeviceTrackingStatus(trackingStatusMap);
            setDeviceMetadataStatus(metadataStatusMap);
            setOffTrackDurations(offTrackDurationsMap);
            setRebootCounts(rebootCountsMap);
            setLastRebootTimestamps(lastRebootTimestampsMap);
            setDeviceSchedules(deviceSchedulesMap);
            return { 
                timestamps: timestampsMap, 
                trackingStatus: trackingStatusMap, 
                metadataStatus: metadataStatusMap,
                offTrackDurations: offTrackDurationsMap,
                rebootCounts: rebootCountsMap,
                lastRebootTimestamps: lastRebootTimestampsMap,
                batteryLevels: batteryLevelsMap,
                deviceSchedules: deviceSchedulesMap
            };
        } catch (err) {
            console.error('Error fetching recording timestamps:', err);
            return { timestamps: {}, trackingStatus: {}, metadataStatus: {}, offTrackDurations: {}, rebootCounts: {}, lastRebootTimestamps: {}, batteryLevels: {}, deviceSchedules: {} };
        }
    };

    const getStatusColor = (priority) => {
        if (!priority) return '#6c757d';
        switch (priority.toLowerCase()) {
            case 'low':
                return '#ffc107';
            case 'medium':
                return '#fd7e14';
            case 'high':
                return '#dc3545';
            default:
                return '#6c757d';
        }
    };

    const renderAlertTooltip = (alertTitle, date) => (props) => (
        <Tooltip id={`tooltip-${alertTitle}`} {...props}>
            {alertTitle}
            <br />
            {date}
        </Tooltip>
    );

    // Format date + time for display in user's local time
    const formatDateTime = (input) => {
        if (!input) return 'N/A';
        try {
            const d = new Date(input);
            const datePart = formatDateString(d.toISOString(), userDateFormat);
            const timePart = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            return `${datePart}, ${timePart}`;
        } catch (e) {
            return 'Invalid Date';
        }
    };

    // Format off-track duration for display
    const formatOffTrackDuration = (durationMinutes) => {
        if (!durationMinutes || durationMinutes <= 0) return '';
        
        const days = Math.floor(durationMinutes / (24 * 60));
        const remainingMinutes = durationMinutes % (24 * 60);
        const hours = Math.floor(remainingMinutes / 60);
        const minutes = Math.floor(remainingMinutes % 60);
        
        const parts = [];
        if (days > 0) parts.push(`${days}d`);
        if (hours > 0) parts.push(`${hours}h`);
        if (minutes > 0) parts.push(`${minutes}m`);
        
        return parts.length > 0 ? parts.join(' ') : '';
    };

    // Format time ago for last reboot
    const formatTimeAgo = (timestamp) => {
        if (!timestamp) return '-';
        
        try {
            const now = new Date();
            const rebootTime = new Date(timestamp);
            const diffMs = now - rebootTime;
            const diffMinutes = Math.floor(diffMs / (1000 * 60));
            
            if (diffMinutes < 60) {
                return `${diffMinutes}m ago`;
            } else if (diffMinutes < 1440) { // Less than 24 hours
                const hours = Math.floor(diffMinutes / 60);
                return `${hours}h ago`;
            } else { // Days
                const days = Math.floor(diffMinutes / 1440);
                return `${days}d ago`;
            }
        } catch (e) {
            return '-';
        }
    };

    const getDeviceAssignments = async (alertsDataParam = null, timestampsParam = null, trackingStatusParam = null, metadataStatusParam = null, fallAlertsDataParam = null, offTrackDurationsParam = null, rebootCountsParam = null, lastRebootTimestampsParam = null, batteryLevelsParam = null, deviceSchedulesParam = null) => {
        try {
            // Use passed parameters if available, otherwise fall back to state
            const alertsToUse = alertsDataParam || alertsData;
            const timestampsToUse = timestampsParam || latestRecordingTimestamps;
            const trackingStatusToUse = trackingStatusParam || deviceTrackingStatus;
            const metadataStatusToUse = metadataStatusParam || deviceMetadataStatus;
            const fallAlertsToUse = fallAlertsDataParam || fallAlertsData;
            const offTrackDurationsToUse = offTrackDurationsParam || offTrackDurations;
            const rebootCountsToUse = rebootCountsParam || rebootCounts;
            const lastRebootTimestampsToUse = lastRebootTimestampsParam || lastRebootTimestamps;
            const batteryLevelsToUse = batteryLevelsParam || {};
            const deviceSchedulesToUse = deviceSchedulesParam || deviceSchedules;
            
            let levels = batteryLevelsToUse;
            const responseData = await httpService.get(`/device/assignments/${groupDeviceId}`);
            if (Array.isArray(responseData.data.data)) {
                let response = [...responseData.data.data];
                for (const item of response) {
                    // Handle null values
                    const patientIdValue = item.patientId || "-";
                    item.patientId = patientIdValue;
                    item.patientName = item.patientName || "-";
                    item.serialNumber = item.serialNumber || "-";
                    item.macAddress = item.macAddress || "-";
                    
                    // Format date using the common utility - only show if device is assigned
                    item.assigned = (item.isAssigned && item.dateAssigned) ? formatDateString(item.dateAssigned, userDateFormat) : "-";

                    // Add status information - check metadata first, then on-track status
                    const actualPatientId = item.patientId !== "-" ? item.patientId : null;
                    const hasMetadata = actualPatientId ? metadataStatusToUse[actualPatientId] : false;
                    let isOnTrack = actualPatientId ? trackingStatusToUse[actualPatientId] : null;
                    let offTrackDuration = actualPatientId ? offTrackDurationsToUse[actualPatientId] : null;
                    const alert = actualPatientId ? alertsToUse[actualPatientId] : null;
                    const latestTimestamp = actualPatientId ? timestampsToUse[actualPatientId] : null;
                    let timeSinceLastActivity = null; // Track total time since last activity
                    
                    // Check if we have alerts or readings
                    const hasAlertsOrReadings = !!alert || !!latestTimestamp;
                    
                    // If metadata is missing but we have readings/alerts, calculate on-track status based on time
                    if (!hasMetadata && hasAlertsOrReadings) {
                        const currentTime = new Date().getTime();
                        let mostRecentTime = null;
                        
                        // Get the most recent timestamp from either alert or reading
                        if (alert) {
                            const alertTimestamp = alert.alert_title.includes("Bluetooth") ? alert.createdDate : alert.alert_lastUpdatedTimestamp;
                            if (alertTimestamp) {
                                mostRecentTime = new Date(alertTimestamp).getTime();
                            }
                        }
                        if (latestTimestamp) {
                            const readingTime = new Date(latestTimestamp).getTime();
                            if (!mostRecentTime || readingTime > mostRecentTime) {
                                mostRecentTime = readingTime;
                            }
                        }
                        
                        // Check if we have device schedule available (even if hasMetadata is false)
                        const deviceSchedule = actualPatientId ? deviceSchedulesToUse[actualPatientId] : null;
                        let thresholdMinutes = 5; // Default threshold when schedule is unknown
                        
                        if (deviceSchedule) {
                            // Use the device schedule to calculate proper threshold
                            const modalityFrequencies = [
                                deviceSchedule.ecgMins,
                                deviceSchedule.ppgMins,
                                deviceSchedule.audioMins,
                                deviceSchedule.tempMins,
                                deviceSchedule.biozMins
                            ].filter(freq => freq != null && freq > 0);
                            
                            if (modalityFrequencies.length > 0) {
                                const fastestFrequency = Math.min(...modalityFrequencies);
                                thresholdMinutes = fastestFrequency + 1; // Same as on-track calculation
                            }
                        }
                        
                        // If we have a timestamp, check if it's within the threshold
                        if (mostRecentTime) {
                            timeSinceLastActivity = (currentTime - mostRecentTime) / (1000 * 60); // minutes
                            isOnTrack = timeSinceLastActivity <= thresholdMinutes;
                            
                            // Calculate off-track duration if device is off track
                            // Store total time since last activity as off-track duration
                            if (!isOnTrack) {
                                offTrackDuration = timeSinceLastActivity;
                            }
                        }
                    }
                    
                    // If there's a more recent reading than any alert, use reading-based status and hide the alert
                    // Only "Overflow" and "Post Motion" alerts allow On Track status when alert is more recent (they send readings)
                    // All other alerts should force Off Track when alert is more recent, UNLESS there's a more recent reading
                    let shouldHideAlert = false; // Flag to hide alert if there's a more recent reading
                    if (alert) {
                        const alertTitle = alert.alert_title || alert.alert_name || '';
                        const alertDisplayName = alert.alert_display_name_business || alert.alert_display_name || '';
                        const alertName = alert.alert_name || '';
                        
                        // Check if this is an Overflow or Post Motion alert (these send readings)
                        const isOverflowOrPostMotion = 
                            alertTitle.toLowerCase().includes('overflow') || 
                            alertTitle.toLowerCase().includes('post motion') ||
                            alertDisplayName.toLowerCase().includes('overflow') ||
                            alertDisplayName.toLowerCase().includes('post motion') ||
                            alertName.toLowerCase() === 'overflow' ||
                            alertName.toLowerCase() === 'post motion';
                        
                        // Get alert timestamp
                        const alertTimestamp = alert.alert_title.includes("Bluetooth") ? alert.createdDate : alert.alert_lastUpdatedTimestamp;
                        const alertTime = alertTimestamp ? new Date(alertTimestamp).getTime() : null;
                        
                        // Check if there's a reading more recent than the alert
                        const readingTime = latestTimestamp ? new Date(latestTimestamp).getTime() : null;
                        const hasMoreRecentReading = readingTime && alertTime && readingTime > alertTime;
                        
                        // If there's a more recent reading than ANY alert, hide the alert and use reading-based status
                        if (hasMoreRecentReading) {
                            shouldHideAlert = true;
                            // Status is already calculated based on reading (either from fetchDeviceTrackingData 
                            // if metadata exists, or from the calculation above if metadata is missing)
                        } else {
                            // If there's no more recent reading, apply alert-based logic
                            // If it's NOT Overflow or Post Motion, force Off Track
                            if (!isOverflowOrPostMotion) {
                                isOnTrack = false;
                                // Calculate off-track duration based on when the alert occurred
                                if (alertTimestamp) {
                                    const currentTime = new Date().getTime();
                                    const timeSinceAlert = (currentTime - alertTime) / (1000 * 60); // minutes
                                    
                                    // Off-track duration is total time since alert occurred
                                    offTrackDuration = timeSinceAlert;
                                }
                            }
                            // If it IS Overflow or Post Motion, allow normal on-track calculation to proceed
                        }
                    }
                    
                    // If no patient ID, show "-"
                    if (!actualPatientId) {
                        item.Status = "-";
                    }
                    // If no alerts and no readings, show "No Data Available"
                    else if (!hasAlertsOrReadings) {
                        item.Status = "No Data Available";
                    }
                    // If device is on track, show "On Track" with green circle
                    else if (isOnTrack === true) {
                        item.Status = (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{
                                    display: 'inline-block',
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: '#28a745',
                                    flexShrink: 0
                                }} />
                                <span>On Track</span>
                            </div>
                        );
                    } 
                    // If device is off track, show "Off Track" with duration
                    else if (isOnTrack === false) {
                        // If duration wasn't calculated, calculate it now based on last activity
                        if (!offTrackDuration && (latestTimestamp || alert)) {
                            const currentTime = new Date().getTime();
                            let mostRecentTime = null;
                            
                            if (alert) {
                                const alertTimestamp = alert.alert_title.includes("Bluetooth") ? alert.createdDate : alert.alert_lastUpdatedTimestamp;
                                if (alertTimestamp) {
                                    mostRecentTime = new Date(alertTimestamp).getTime();
                                }
                            }
                            if (latestTimestamp) {
                                const readingTime = new Date(latestTimestamp).getTime();
                                if (!mostRecentTime || readingTime > mostRecentTime) {
                                    mostRecentTime = readingTime;
                                }
                            }
                            
                            if (mostRecentTime) {
                                offTrackDuration = (currentTime - mostRecentTime) / (1000 * 60); // minutes
                            }
                        }
                        
                        const durationText = formatOffTrackDuration(offTrackDuration);
                        item.Status = (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{
                                    display: 'inline-block',
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: '#dc3545',
                                    flexShrink: 0
                                }} />
                                <span>Off Track {durationText ? `(${durationText})` : ''}</span>
                            </div>
                        );
                    }
                    // Default fallback
                    else {
                        item.Status = "-";
                    }

                    // Add error status information (alerts)
                    // Check if alert should be shown based on device status and scheduled reading time frame
                    const shouldShowAlert = () => {
                        if (!alert) {
                            return false;
                        }
                        
                        const latestTimestamp = actualPatientId ? timestampsToUse[actualPatientId] : null;
                        const deviceSchedule = actualPatientId ? deviceSchedulesToUse[actualPatientId] : null;
                        
                        // For "Bluetooth Error Alert", use createdDate instead of alert_lastUpdatedTimestamp
                        const isBluetoothError = alert.alert_title.includes("Bluetooth");
                        const alertTimestamp = isBluetoothError ? alert.createdDate : alert.alert_lastUpdatedTimestamp;
                        
                        const alertTime = new Date(alertTimestamp).getTime();
                        const currentTime = new Date().getTime();
                        const timeSinceAlert = currentTime - alertTime;
                        
                        // Always show very recent alerts (within last 5 minutes) regardless of schedule
                        // This handles cases where alerts are just triggered
                        if (timeSinceAlert >= 0 && timeSinceAlert <= 5 * 60 * 1000) {
                            return true;
                        }
                        
                        // Need schedule and alert timestamp to check if it's within schedule
                        if (!deviceSchedule || !alertTimestamp) {
                            return false;
                        }
                        
                        // Get the fastest frequency from device schedule
                        const modalityFrequencies = [
                            deviceSchedule.ecgMins,
                            deviceSchedule.ppgMins,
                            deviceSchedule.audioMins,
                            deviceSchedule.tempMins,
                            deviceSchedule.biozMins
                        ].filter(freq => freq != null && freq > 0);
                        
                        if (modalityFrequencies.length === 0) {
                            return false; // No valid schedule
                        }
                        
                        const fastestFrequency = Math.min(...modalityFrequencies);
                        
                        // Calculate time window: fastest frequency + 1 minute margin (same as on-track calculation)
                        const timeWindowMinutes = fastestFrequency + 1;
                        const timeWindowMs = timeWindowMinutes * 60 * 1000;
                        
                        // For both on-track and off-track devices, check if alert falls within scheduled reading time frame
                        if (isOnTrack === true) {
                            // Device is on track - check if alert is within reading cycle window
                            if (!latestTimestamp) {
                                // No latest reading but alert is recent - show it if within reasonable window
                                // Use a default window of 30 minutes if no schedule context
                                const defaultWindow = 30 * 60 * 1000; // 30 minutes
                                return timeSinceAlert >= 0 && timeSinceAlert <= defaultWindow;
                            }
                            
                            const latestReadingTime = new Date(latestTimestamp).getTime();
                            const timeSinceLatestReading = currentTime - latestReadingTime;
                            
                            // If latest reading is very recent (within schedule window), alert should also be recent
                            // If latest reading is older but device is still on track, check if alert is within reading cycle
                            if (timeSinceLatestReading <= timeWindowMs) {
                                // Latest reading is recent (within schedule), alert should be within 2x the schedule window
                                return timeSinceAlert >= 0 && timeSinceAlert <= timeWindowMs * 2;
                            } else {
                                // Latest reading is older but device is on track, check if alert occurred around reading time
                                // Allow alert if it's within 2x the schedule window around the latest reading
                                const timeDiffFromLatestReading = Math.abs(alertTime - latestReadingTime);
                                return timeDiffFromLatestReading <= timeWindowMs * 2;
                            }
                        } else if (isOnTrack === false) {
                            // Device is off track - check if alert falls within the latest device schedule
                            // Check if alert occurred within a reasonable window around when readings were expected
                            if (latestTimestamp) {
                                const latestReadingTime = new Date(latestTimestamp).getTime();
                                // Check if alert occurred within the reading cycle window around the latest reading
                                // Allow alerts within 2x the schedule window around the latest reading time
                                const timeDiffFromLatestReading = Math.abs(alertTime - latestReadingTime);
                                return timeDiffFromLatestReading <= timeWindowMs * 2;
                            } else {
                                // No latest reading, check if alert is recent (within last 2x schedule window)
                                return timeSinceAlert >= 0 && timeSinceAlert <= timeWindowMs * 2;
                            }
                        }
                        
                        return false;
                    };
                    
                    // Handle error status display
                    // If there's a more recent reading than the alert, hide the alert
                    // Show "No Data Transferred" if Off Track, otherwise show "-"
                    if (shouldHideAlert) {
                        if (isOnTrack === false) {
                            // If Off Track, show "No Data Transferred" with tooltip
                            const renderNoDataTooltip = (props) => (
                                <Tooltip id="tooltip-no-data-transferred" {...props}>
                                    Connection Not Made
                                    <br />
                                    - Away from phone
                                    <br />
                                    - Phone/Patch is off
                                </Tooltip>
                            );
                            
                            item.errorStatus = (
                                <OverlayTrigger
                                    placement="top"
                                    delay={{ show: 50, hide: 50 }}
                                    overlay={renderNoDataTooltip}
                                >
                                    <span style={{ cursor: 'help' }}>No Data Transferred</span>
                                </OverlayTrigger>
                            );
                        } else {
                            // If On Track, show "-"
                            item.errorStatus = "-";
                        }
                    } else if (!alert) {
                        // No alert at all - show "-"
                        item.errorStatus = "-";
                    } else if (hasMetadata === false) {
                        // No metadata available, but we have an alert
                        // Always show device status alerts (like Fast Recovery, ECG Saturation) even without metadata
                        // For other alerts, show if they're recent (within last 5 minutes)
                        const isDeviceStatusAlert = alert.alert_subType === "device status";
                        const alertTimestamp = alert.alert_title.includes("Bluetooth") ? alert.createdDate : alert.alert_lastUpdatedTimestamp;
                        
                        if (alertTimestamp) {
                            const alertTime = new Date(alertTimestamp).getTime();
                            const currentTime = new Date().getTime();
                            const timeSinceAlert = currentTime - alertTime;
                            
                            // Show device status alerts always, or recent alerts (within last 5 minutes)
                            if (isDeviceStatusAlert || (timeSinceAlert >= 0 && timeSinceAlert <= 5 * 60 * 1000)) {
                                // Show the alert even without metadata
                                const alertDate = formatDateString(alertTimestamp, userDateFormat);
                                const alertTimeStr = new Date(alertTimestamp).toLocaleTimeString('en-US', { 
                                    hour: '2-digit', 
                                    minute: '2-digit',
                                    hour12: true 
                                });
                                const alertTitle = alert.alert_display_name_business || alert.alert_title || alert.alert_name || 'Unknown Alert';
                                const tooltipDate = `${alertDate} ${alertTimeStr}`;
                                
                                item.errorStatus = (
                                    <OverlayTrigger
                                        placement="top"
                                        delay={{ show: 50, hide: 50 }}
                                        overlay={renderAlertTooltip(alert.alert_description_business, tooltipDate)}
                                    >
                                        <div 
                                            style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                                        >
                                            <span style={{
                                                display: 'inline-block',
                                                width: '8px',
                                                height: '8px',
                                                borderRadius: '50%',
                                                backgroundColor: getStatusColor(alert.alert_priority),
                                                flexShrink: 0
                                            }} />
                                            <span style={{ 
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {alertTitle}
                                            </span>
                                        </div>
                                    </OverlayTrigger>
                                );
                            } else {
                                item.errorStatus = "-";
                            }
                        } else {
                            item.errorStatus = "-";
                        }
                    } else if (shouldShowAlert()) {
                        // Alert exists and falls within scheduled reading time frame - show alert
                        // For "Bluetooth Error Alert", use createdDate instead of alert_lastUpdatedTimestamp
                        const isBluetoothError = alert.alert_title.includes("Bluetooth");
                        const alertTimestamp = isBluetoothError ? alert.createdDate : alert.alert_lastUpdatedTimestamp;
                        const alertDate = alertTimestamp ? formatDateString(alertTimestamp, userDateFormat) : 'Unknown';
                        const alertTime = alertTimestamp ? new Date(alertTimestamp).toLocaleTimeString('en-US', { 
                            hour: '2-digit', 
                            minute: '2-digit',
                            hour12: true 
                        }) : '';
                        const alertTitle = alert.alert_display_name_business || alert.alert_title || alert.alert_name || 'Unknown Alert';
                        const tooltipDate = `${alertDate} ${alertTime}`;
                        
                        item.errorStatus = (
                            <OverlayTrigger
                                placement="top"
                                delay={{ show: 50, hide: 50 }}
                                overlay={renderAlertTooltip(alert.alert_description_business, tooltipDate)}
                            >
                                <div 
                                    style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                                >
                                    <span style={{
                                        display: 'inline-block',
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                        backgroundColor: getStatusColor(alert.alert_priority),
                                        flexShrink: 0
                                    }} />
                                    <span style={{ 
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {alertTitle}
                                    </span>
                                </div>
                            </OverlayTrigger>
                        );
                    } else {
                        // Alert exists but doesn't fall within scheduled reading time frame
                        if (isOnTrack === true) {
                            // If On Track and alert is outside schedule, show "-"
                            item.errorStatus = "-";
                        } else {
                            // If Off Track and alert is outside schedule, show "No Data Transferred" with tooltip
                            const renderNoDataTooltip = (props) => (
                                <Tooltip id="tooltip-no-data-transferred" {...props}>
                                    Connection Not Made
                                    <br />
                                    - Away from phone
                                    <br />
                                    - Phone/Patch is off
                                </Tooltip>
                            );
                            
                            item.errorStatus = (
                                <OverlayTrigger
                                    placement="top"
                                    delay={{ show: 50, hide: 50 }}
                                    overlay={renderNoDataTooltip}
                                >
                                    <span style={{ cursor: 'help' }}>No Data Transferred</span>
                                </OverlayTrigger>
                            );
                        }
                    }

                    // Add reboot count and last reboot time from metadata
                    const rebootCount = actualPatientId ? rebootCountsToUse[actualPatientId] : null;
                    const lastRebootTimestamp = actualPatientId ? lastRebootTimestampsToUse[actualPatientId] : null;
                    
                    item.rebootCount = rebootCount !== null && rebootCount !== undefined ? rebootCount : "-";
                    item.lastReboot = formatTimeAgo(lastRebootTimestamp);

                    // Add fall detection information
                    const fallAlert = actualPatientId ? fallAlertsToUse[actualPatientId] : null;
                    if (fallAlert) {
                        const fallDate = fallAlert.alert_lastUpdatedTimestamp ? formatDateString(fallAlert.alert_lastUpdatedTimestamp, userDateFormat) : 'Unknown';
                        const fallTime = fallAlert.alert_lastUpdatedTimestamp ? new Date(fallAlert.alert_lastUpdatedTimestamp).toLocaleTimeString('en-US', { 
                            hour: '2-digit', 
                            minute: '2-digit',
                            hour12: true 
                        }) : '';
                        const fallTitle = fallAlert.alert_title || fallAlert.alert_name || 'Fall Detected';
                        const tooltipDate = `${fallDate} ${fallTime}`;
                        
                        item.fallDetection = (
                            <OverlayTrigger
                                placement="top"
                                delay={{ show: 50, hide: 50 }}
                                overlay={renderAlertTooltip(fallTitle, tooltipDate)}
                            >
                                <div 
                                    style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                                >
                                    <span style={{
                                        display: 'inline-block',
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                        backgroundColor: getStatusColor(fallAlert.alert_priority),
                                        flexShrink: 0
                                    }} />
                                    <span style={{ 
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {fallTitle}
                                    </span>
                                </div>
                            </OverlayTrigger>
                        );
                    } else {
                        item.fallDetection = "-";
                    }

                    // Add latest recording timestamp
                    const timestamp = actualPatientId ? timestampsToUse[actualPatientId] : null;
                    item.latestRecording = timestamp ? formatDateTime(timestamp) : "-";

                    if (deviceName === "Perin Patch") {
                        const batteryLevel = levels[item.patientId];
                        const isDecimal = batteryLevel !== -1 && batteryLevel && batteryLevel <= 1;
                        const normalizedLevel = isDecimal ? Math.floor(batteryLevel * 100) : Math.floor(batteryLevel);
                        item.statusText = batteryLevel !== -1 && batteryLevel ? 
                            `${normalizedLevel}%` 
                            : "-"; // Store text version of status
                        const batteryIcon = configureBatteryIcon(batteryLevel);
                        item.status = (
                            <span style={{ display: "flex", alignItems: "center", columnGap: "5px" }}>
                                {(batteryLevel === -1 || !batteryLevel) ?
                                    "-" : batteryIcon
                                }
                                {item.statusText}
                            </span>
                        );
                    }
                }
                setDeviceData(response);
            } else {
                console.error('Invalid data format:');
            }
        } catch (err) {
            console.log(err);
        }
    };

    const onBackHandler = () => {
        navigate('/device-management');
    };

    const tabStyle = (isActive) => ({
        paddingLeft: 20,
        paddingRight: 20,
        paddingTop: 4,
        paddingBottom: 4,
        background: isActive ? 'rgba(203, 243, 210, 0.30)' : '#F3F3F3',
        borderBottom: isActive ? '3px #006969 solid' : '0.50px #B9B9B9 solid',
        cursor: 'pointer'
    });

    const handleFormChange = (field) => (event) => {
        setCheckoutForm({
            ...checkoutForm,
            [field]: event.target.value
        });
    };

    const handleCheckout = async () => {
        try {
            // Validate form data
            if (!checkoutForm.name.trim()) {
                alert('Please enter a name');
                return;
            }
            if (!checkoutForm.reason.trim()) {
                alert('Please enter a reason');
                return;
            }
            if (!checkoutForm.system.trim()) {
                alert('Please enter a system');
                return;
            }

            // Make API calls for each selected device
            const promises = selectedRows.map(async (selectedDevice) => {
                try {
                    // Get the device registry id
                    const deviceRegistryId = selectedDevice.id;
                    
                    if (!deviceRegistryId) {
                        console.error('Missing id for device:', selectedDevice);
                        throw new Error('Invalid device data');
                    }

                    await httpService.post(`/device/checkout/${deviceRegistryId}`, {
                        userName: checkoutForm.name,
                        reason: checkoutForm.reason,
                        system: checkoutForm.system
                    });

                } catch (error) {
                    console.error(`Error checking out device ${selectedDevice.id}:`, error);
                    throw error;
                }
            });

            // Wait for all checkouts to complete
            await Promise.all(promises);

            // Refresh the appropriate data based on active tab
            if (activeTab === 'checkoutLog') {
                await getCheckoutLog();
            } else {
                // Fetch alerts and tracking data in parallel
                const [alertsData, trackingData] = await Promise.all([
                    fetchAlertsForAllDevices(),
                    fetchLatestRecordingTimestamps()
                ]);
                await getDeviceAssignments(alertsData.alerts, trackingData.timestamps, trackingData.trackingStatus, trackingData.metadataStatus, alertsData.fallAlerts, trackingData.offTrackDurations, trackingData.rebootCounts, trackingData.lastRebootTimestamps, trackingData.batteryLevels);
            }
            
            // Reset form and close modal
            setShowCheckoutModal(false);
            setCheckoutForm({ name: '', reason: '', system: '' });
            
            // Clear selected rows
            dispatch(setSelectedRows([]));

        } catch (error) {
            console.error('Error during checkout:', error);
            alert('Failed to check out one or more devices. Please try again.');
        }
    };

    const modalStyle = {
        position: 'absolute',
        top: '115px',
        right: '45px',
        width: 400,
        bgcolor: 'background.paper',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
        p: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
    };

    // Add function to fetch checkout log data
    const getCheckoutLog = async () => {
        try {
            const response = await httpService.get(`/device/checkout-log/${groupDeviceId}`);
            if (Array.isArray(response.data.data)) {
                const formattedData = response.data.data.map(item => {
                    // Convert UTC string to local time
                    const checkoutUTC = item.checkoutDate ? new Date(item.checkoutDate) : null;
                    const checkinUTC = item.checkinDate ? new Date(item.checkinDate) : null;
                    
                    // Adjust for local timezone
                    const checkoutLocal = checkoutUTC ? new Date(checkoutUTC.getTime() - (checkoutUTC.getTimezoneOffset() * 60000)) : null;
                    const checkinLocal = checkinUTC ? new Date(checkinUTC.getTime() - (checkinUTC.getTimezoneOffset() * 60000)) : null;

                    return {
                        ...item,
                        checkoutDate: checkoutLocal ? checkoutLocal.toLocaleString("en-US", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true
                        }) : "-",
                        checkinDate: checkinLocal ? checkinLocal.toLocaleString("en-US", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true
                        }) : "-",
                        status: (
                            <span style={{
                                color: item.status === 'Checked Out' ? '#FF4444' : '#006969',
                                fontWeight: '500'
                            }}>
                                {item.status}
                            </span>
                        )
                    };
                });
                setCheckoutLogData(formattedData);
            }
        } catch (err) {
            console.error('Error fetching checkout log:', err);
        }
    };

    // Define headers for each tab
    const assignedHeaders = deviceName === "Perin Patch" ? [
        { key: 'patientId', label: "ID"},
        { key: 'patientName', label: "Assigned to"},
        { key: 'serialNumber', label: "Serial Number"},
        { key: 'macAddress', label: "Mac Address"},
        { key: 'assigned', label: "Date Assigned"},
        { key: 'status', label: "Battery Status"},
        { key: 'Status', label: "Status"},
        { key: 'errorStatus', label: "Error Status"},
        { key: 'fallDetection', label: "Fall Detection"},
        { key: 'latestRecording', label: "Latest Recording"},
        { key: 'rebootCount', label: "Reboot Count"},
        { key: 'lastReboot', label: "Last Reboot"},
    ] : [
        { key: 'patientId', label: "ID"},
        { key: 'patientName', label: "Assigned to"},
        { key: 'serialNumber', label: "Serial Number"},
        { key: 'macAddress', label: "Mac Address"},
        { key: 'assigned', label: "Date Assigned"},
    ];

    const unassignedHeaders = [
        { key: 'serialNumber', label: "Serial Number"},
        { key: 'macAddress', label: "Mac Address"},
        { key: 'assigned', label: "Date Assigned"},
    ];

    const checkoutLogHeaders = [
        { key: 'serialNumber', label: "Serial Number"},
        { key: 'status', label: "Battery Status"},
        { key: 'userName', label: "Name"},
        { key: 'reason', label: "Reason"},
        { key: 'software', label: "Software"},
        { key: 'checkoutDate', label: "Check Out Date"},
        { key: 'checkinDate', label: "Check In Date"}
    ];

    useEffect(() => {
        // Set all headers as selected whenever tab changes
        let currentHeaders;
        switch(activeTab) {
            case 'assigned':
                currentHeaders = assignedHeaders;
                break;
            case 'unassigned':
                currentHeaders = unassignedHeaders;
                break;
            case 'checkoutLog':
                currentHeaders = checkoutLogHeaders;
                break;
            default:
                currentHeaders = assignedHeaders;
        }
        
        dispatch(setSelectedHeaders(currentHeaders));
    }, [activeTab]);

    return (
        <div className={managementStyles.MiddleContent}>
            <div style={{width: '100%', borderBottom: '0.50px #B9B9B9 solid', marginTop: '20px'}}>
                <div style={{height: 32, display: 'flex', justifyContent: 'space-between'}}>
                    <div style={{display: 'flex'}}>
                        <div 
                            style={tabStyle(activeTab === 'assigned')}
                            onClick={() => setActiveTab('assigned')}
                        >
                            <span style={{
                                color: activeTab === 'assigned' ? 'black' : '#373737',
                                fontSize: 16,
                                fontFamily: 'Roboto',
                                fontWeight: '400',
                                lineHeight: '24px',
                                letterSpacing: 0.04
                            }}>Assigned</span>
                        </div>
                        <div 
                            style={tabStyle(activeTab === 'unassigned')}
                            onClick={() => setActiveTab('unassigned')}
                        >
                            <span style={{
                                color: activeTab === 'unassigned' ? 'black' : '#373737',
                                fontSize: 16,
                                fontFamily: 'Roboto',
                                fontWeight: '400',
                                lineHeight: '24px',
                                letterSpacing: 0.04
                            }}>Unassigned</span>
                        </div>

                    </div>
                    

                </div>
            </div>
            <div style={{display:"flex", alignItems:"center", columnGap:"5px", marginTop: "20px"}}>
                {activeTab !== 'checkoutLog' && (
                    <>
                        <span className="subtitle-1">{`${groupName} |`}</span>
                        <span className={titleStyles.HubsNumber}>{filteredDeviceData.length}</span>
                        <span className={titleStyles.HubsLabel}>/{totalDevices}</span>
                        <span className={titleStyles.HubsLabel} style={{color:"#30584A"}}>{`${deviceName} ${activeTab === 'assigned' ? 'Assigned' : 'Unassigned'}`}</span>
                    </>
                )}
            </div>
            <FilterManagementView
                isOrganization={false}
                isGroup={false}
                isSubGroup={false}
                isTeam={false}
                showDeleteButton={false}
                showAddButton={false}
                modelHeader='Delete Device?' 
            />
            <div className={managementStyles.GridContainer}>
                <PerinHealthGrid
                    dataList={activeTab === 'checkoutLog' ? checkoutLogData : filteredDeviceData}
                    headers={activeTab === 'checkoutLog' ? checkoutLogHeaders : 
                            activeTab === 'assigned' ? assignedHeaders : unassignedHeaders}
                    showCheckbox={true}
                    showEditIcon={false}
                    showHeaderFilter={true}
                    gridHeight="100%"
                    onRowValueClick={deviceName === "Perin Patch" && activeTab !== 'checkoutLog' ? handlePatientNameClick : undefined}
                    rowValueAction={deviceName === "Perin Patch" && activeTab === 'assigned' ? 'patientName' : (deviceName === "Perin Patch" && activeTab === 'unassigned' ? 'serialNumber' : undefined)}
                />
            </div>
            <Modal
                open={showCheckoutModal}
                onClose={() => setShowCheckoutModal(false)}
                BackdropProps={{
                    style: { backgroundColor: 'transparent' }
                }}
                disableAutoFocus
            >
                <Box sx={modalStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <h4 style={{ margin: 0 }}>Device Check Out Card</h4>
                        <span 
                            onClick={() => setShowCheckoutModal(false)}
                            style={{ 
                                cursor: 'pointer', 
                                fontSize: '30px',
                                marginTop: '-8px'
                            }}
                        >
                            ×
                        </span>
                    </div>
                    <TextField
                        label="Name"
                        value={checkoutForm.name}
                        onChange={handleFormChange('name')}
                        fullWidth
                        variant="outlined"
                        size="small"
                    />
                    <TextField
                        label="Reason"
                        value={checkoutForm.reason}
                        onChange={handleFormChange('reason')}
                        fullWidth
                        variant="outlined"
                        size="small"
                    />
                    <TextField
                        label="System"
                        value={checkoutForm.system}
                        onChange={handleFormChange('system')}
                        fullWidth
                        variant="outlined"
                        size="small"
                    />
                    <Button 
                        variant="contained"
                        onClick={handleCheckout}
                        fullWidth
                        style={{
                            backgroundColor: '#006969',
                            color: 'white',
                            marginTop: '10px',
                            textTransform: 'none'
                        }}
                    >
                        Check Out
                    </Button>
                </Box>
            </Modal>

        </div>
    )
}

export default DeviceAssignment;

